import { RoleInputFilter } from "domain/models/role-input-filter";
import { translateText } from "infra/translation/translate";
import Spinner from "presentation/components/Shared/Spinner";
import DistanceSetting from "presentation/components/Tripsheet/TripsheetGrid/TripsheetDistacyGrid";
import TripsheetDocumentsGrid from "presentation/components/Tripsheet/TripsheetGrid/TripsheetDocumentsGrid";
import TripsheetMonthGrid from "presentation/components/Tripsheet/TripsheetGrid/TripsheetMonthGrid";
import TripsheetSettingsGrid from "presentation/components/Tripsheet/TripsheetGrid/TripsheetSettingGrid";
import usePermissionCheck from "presentation/hooks/use-check-permissions";
import { useRoleByFilter } from "presentation/hooks/use-query-role";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { TabPanel, TabView } from "primereact/tabview";
import { Toast } from "primereact/toast";
import React, { useMemo, useRef } from "react";
import FunctionHeader from "../../components/Header/FunctionHeader";
import SupportTripTab from "presentation/components/Tripsheet/TripsheetGrid/SupportTrips";

const TripSheetGridPage = ({ children }: { children?: React.ReactNode }) => {
  const adminRolesAllowed = ["Admin", "CEO", "Finanças"];
  const supportTripRolesAllowed = ["Colaborador com Viagens de Suporte"];
  const freeTripRolesAllowed = ["Colaborador com Viagens Livres"];
  const { isLoading: isPermissionLoading } = usePermissionCheck({
    requiredFunction: "tripsheets",
  });
  const { userPermissions } = useAuthStore();
  const { data: rolesData, isLoading: areRolesLoading } = useRoleByFilter({
    limit: 100,
  } as RoleInputFilter);
  const [activeIndex, setActiveIndex] = React.useState(0);

  const hasAdminRolePermission = useMemo(() => {
    if (!rolesData?.data || !userPermissions) {
      return false;
    }
    const allowedRoleIds = rolesData.data
      .filter((role) => adminRolesAllowed.includes(role.name))
      .map((role) => role.id);

    const hasPermission = userPermissions.some((permission) =>
      allowedRoleIds.includes(permission.roleId)
    );
    return hasPermission;
  }, [rolesData, userPermissions]);

  const hasSupportTripRolePermission = useMemo(() => {
    if (!rolesData?.data || !userPermissions) {
      return false;
    }
    const allowedRoleIds = rolesData.data
      .filter((role) => supportTripRolesAllowed.includes(role.name))
      .map((role) => role.id);

    const hasPermission = userPermissions.some((permission) =>
      allowedRoleIds.includes(permission.roleId)
    );
    return hasPermission;
  }, [rolesData, userPermissions]);

  const hasFreeTripRolePermission = useMemo(() => {
    if (!rolesData?.data || !userPermissions) {
      return false;
    }
    const allowedRoleIds = rolesData.data
      .filter((role) => freeTripRolesAllowed.includes(role.name))
      .map((role) => role.id);

    const hasPermission = userPermissions.some((permission) =>
      allowedRoleIds.includes(permission.roleId)
    );
    return hasPermission;
  }, [rolesData, userPermissions]);

  React.useEffect(() => {
    if (hasAdminRolePermission) {
      setActiveIndex(0); // primeira tab admin
    } else if (hasSupportTripRolePermission || hasFreeTripRolePermission) {
      setActiveIndex(3); // primeira tab de suporte
    } else {
      setActiveIndex(4); // Documentos
    }
  }, [hasAdminRolePermission, hasSupportTripRolePermission, hasFreeTripRolePermission]);

  const toast = useRef<Toast>(null);
  if (isPermissionLoading || areRolesLoading || !userPermissions) {
    return <Spinner />;
  }
  return (
    <div>
      <FunctionHeader title="Mapa de KMs" />
      {/* <TripsheetGrid /> */}
      <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
        {hasAdminRolePermission && (
          <TabPanel
            key="settings"
            header={translateText("Settings", "pt")}
            leftIcon="pi pi-cog mr-2"
          >
            <TripsheetSettingsGrid />
          </TabPanel>
        )}
        {hasAdminRolePermission && (
          <TabPanel
            key="trips"
            header={translateText("Trips", "pt")}
            leftIcon="pi pi-map mr-2"
          >
            <TripsheetMonthGrid />
          </TabPanel>
        )}
        {hasAdminRolePermission && (
          <TabPanel
            key="distance"
            header={translateText("Distância entre cidades", "pt")}
            leftIcon="pi pi-map mr-2"
          >
            <DistanceSetting />
          </TabPanel>
        )}
        {(hasSupportTripRolePermission || hasFreeTripRolePermission || hasAdminRolePermission) && [
          <TabPanel
            key="supportTrips"
            header={
              (hasSupportTripRolePermission)
                ? translateText("Deslocações de Suporte", "pt")
                : translateText("Deslocações Extra", "pt")
            }
            leftIcon="pi pi-car mr-2"
          >
            <SupportTripTab
              isAdmin={hasAdminRolePermission}
              isFree={hasFreeTripRolePermission}
            />
          </TabPanel>
        ]}
        <TabPanel
          header={translateText("Documentos", "pt")}
          leftIcon="pi pi-file mr-2"
        >
          <TripsheetDocumentsGrid />
        </TabPanel>
      </TabView>
      <div className="default-4-cols next-button"></div>
      <Toast ref={toast} position="bottom-right" />
    </div>
  );
};

export default TripSheetGridPage;
